import React, { useMemo } from 'react';
import NumberFormat from 'react-number-format';
import { getGetIdFn, noop } from '../utils';
import { BaseInputProps } from '../shared-types';
import BaseInput from '../BaseInput/BaseInput';
import InputControl from '../InputControl/InputControl';

export type InputAttributes = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'defaultValue' | 'value' | 'children'
>;

export type Props = BaseInputProps & {
  componentName?: string;
  maxAmount?: number;
  minAmount?: number;
  suffix?: string;
  prefix?: string;
  placeholder?: string;
  thousandsGroupStyle?: 'thousand' | 'lakh' | 'wan';
  thousandSeparator?: string;
  decimalSeparator?: string;
  decimalScale?: number;
  allowNegative?: boolean;
  onHintToggle?: (open: boolean) => void;
  value?: string;
  ariaLabel?: string;
  autoFocus?: boolean;
  eventId?: string;
};

const FormattedNumericInput: React.FC<Props> = ({
  onChange = noop,
  onHintToggle = noop,
  thousandsGroupStyle,
  decimalSeparator,
  decimalScale,
  thousandSeparator,
  label,
  required,
  value,
  componentName = 'FormattedNumericInput',
  maxAmount = Infinity,
  minAmount = -Infinity,
  hint,
  ariaLabel,
  suffix,
  prefix,
  placeholder,
  allowNegative,
  autoFocus,
  error,
  disabled,
  readOnly,
  'data-testid': dataTestID,
  eventId,
}: Props) => {
  // END focus/blur logic

  const id = useMemo(
    () => getGetIdFn(componentName)(label),
    [componentName, label]
  );

  return (
    <InputControl
      label={label}
      required={required}
      id={id}
      hint={hint}
      onHintToggle={onHintToggle}
      errorMessage={error}
      eventId={eventId}
    >
      <NumberFormat
        data-testid={dataTestID || id}
        ariaLabel={ariaLabel}
        thousandsGroupStyle={thousandsGroupStyle}
        decimalScale={decimalScale}
        decimalSeparator={decimalSeparator}
        thousandSeparator={thousandSeparator}
        suffix={suffix}
        readOnly={readOnly}
        prefix={prefix}
        disabled={disabled}
        placeholder={placeholder}
        value={value}
        allowNegative={allowNegative}
        inputMode="decimal"
        autoFocus={autoFocus}
        customInput={BaseInput}
        componentName="FormattedNumericInput"
        onValueChange={({ value: fieldValue }) => onChange(fieldValue)}
        isAllowed={({ floatValue }) =>
          typeof floatValue === 'undefined'
            ? true
            : floatValue <= maxAmount && floatValue >= minAmount
        }
      />
    </InputControl>
  );
};

export default FormattedNumericInput;
